export interface IResettableOrder {
    id: number;
    updated_at?: string | null;
    order_references: { id: number }[];
    order_mix_entries: { id: number }[];
    order_print_inks: { id: number }[];
}

const nestedIds = (rows?: { id: number }[]): string => (rows ?? []).map(r => r.id).join(".");

// Identity of the server payload the edit form is synced to. Save & Continue
// keeps the same order id, so keying a reset on the id alone never re-syncs and
// the rows created by that save stay at id 0 in form state — they would be
// created again on the next save. The nested ids are part of the key because a
// nested-only save does not necessarily bump the parent's updated_at.
export const orderFormResetKey = (order?: IResettableOrder | null): string => {
    if (!order) return "none";
    return [
        order.id,
        order.updated_at,
        nestedIds(order.order_references),
        nestedIds(order.order_mix_entries),
        nestedIds(order.order_print_inks),
    ].join("-");
}

// The order id is the first segment of the key, so it survives the split even
// though updated_at also contains dashes.
const orderIdFromKey = (key: string): string => key.split("-", 1)[0];

// A validation failure re-renders the form from the in-memory order: rows the
// user marked with `_destroy` come back without the marker and rows they added
// come back with a null id, so the reset key moves even though the user never
// navigated. Re-syncing there would resurrect the deleted rows and drop every
// value typed since, so a render carrying server errors only re-syncs when it
// is a different order.
export const shouldResetOrderForm = (
    syncedKey: string | null,
    nextKey: string,
    hasServerErrors: boolean,
): boolean => {
    if (syncedKey === null) return true;
    if (syncedKey === nextKey) return false;
    if (!hasServerErrors) return true;
    return orderIdFromKey(syncedKey) !== orderIdFromKey(nextKey);
}
